import { useState, useEffect, useMemo } from 'react'
import { connect, useDispatch } from 'react-redux'
import ConfirmationDialog from '../confirmation-dialog/Confirmation'
import { makeStyles, useTheme } from '@mui/styles'
import { getSettingsStyles } from '../../styles/settingsStyles'
import {
  getAllSetups,
  deleteSetup,
  createSetup,
  updateSetup,
} from '../../redux/actions/setups'
import {
  CustomInputLabel,
  CustomTextField,
} from '../../components/restyled-mui/CustomInputs'

import {
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  Checkbox,
  Button,
  DialogActions,
  Box,
  TextField,
  Switch,
  Typography,
  Grid,
  CircularProgress,
  Tooltip,
  IconButton,
  Tab,
  List,
  ListItemButton,
  ListItemText,
  InputAdornment,
} from '@mui/material'
import {
  CustomButton,
  PrimaryButton,
} from '../../components/restyled-mui/CustomButton'
import Navigation from '../settings-navigation/Navigation'
import HelpIcon from '@mui/icons-material/Help'
import ClearIcon from '@mui/icons-material/Clear'
import FormHelperText from '@mui/material/FormHelperText'
import Api from '../../api'
import countries from 'world-countries'
import {
  openQmDialog,
  submitQmUpload,
  closeQmDialog,
  resetQmState,
} from '../../redux/actions/setupThreshold'
import { TabContext, TabList, TabPanel } from '@mui/lab'

const useStyles = makeStyles(theme => ({
  ...getSettingsStyles(theme),
  mainContainer: {
    width: '90%',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
  },
  headerContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'end',
    padding: '20px 0 15px 0',
  },
  subsection: {
    color: theme.palette.primary.main,
  },
  tooltipContainer: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  tooltipContainerSD: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '5px',
  },
  tooltip: {
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    fontSize: theme.typography.fontSize,
  },
  arrow: {
    color: theme.palette.primary.main,
  },
  helpIcon: {
    color: theme.palette.primary.main,
  },
  toggleButtonAggDef: {
    '&.Mui-selected': {
      backgroundColor: '#368ED8',
      color: 'white',
      '&:hover': {
        backgroundColor: '#2771a7',
      },
    },
    '&.Mui-selected.Mui-disabled': {
      backgroundColor: '#ccc',
      color: 'white !important',
      opacity: 0.7, // optional for disabled look
      pointerEvents: 'none',
    },
  },
  centerLoading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
  },
  dialogContent: {
    margin: '20px 0px 20px 0px',
  },
  qmUploadBtn: {
    width: '200px',
    height: '40px',
    marginTop: '25px',
  },

  qmTitle: {
    textAlign: 'center',
    fontSize: '1.1rem',
    fontWeight: 500,
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
  },
  qmInput: {
    fontSize: '1rem',
    fontWeight: 500,
  },
  factorInput: {
    maxWidth: 100,
    '& .MuiFormHelperText-root': {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      marginTop: 4,
      lineHeight: 1,
      fontSize: '0.75rem',
    },
  },
  fileInput: {
    flex: 0.8, // take all available space
    minWidth: 0, // prevents weird overflow in flex
  },
  threhsoldUpdates: {
    border: '1px solid #ddd',
    borderRadius: 1,
    marginTop: 1,
    padding: 2,
    backgroundColor: '#fafafa',
    maxHeight: 250, // adjust as needed (px)
    overflowY: 'auto',
  },
  noThresholdUpdate: {
    marginBottom: 0.5,
    fontStyle: 'italic',
    color: '#999',
  },
  usersList: {
    border: `1px solid ${theme.palette.table.cellBorder}`,
    marginTop: 8,
    padding: 0,
    maxHeight: 450,
    overflowY: 'auto',
  },
  userListItem: {
    borderBottom: `1px solid ${theme.palette.table.cellBorder}`,
    paddingTop: 2,
    paddingBottom: 2,
    '&:last-child': {
      borderBottom: 'none',
    },
    '&:nth-of-type(even)': {
      backgroundColor: theme.palette.table.cellBg,
    },
  },
  noUsersMatch: {
    padding: '8px 12px',
    fontStyle: 'italic',
    color: '#999',
  },
  thresholdMessageOk: {
    marginTop: 10,
    color: 'green',
    fontWeight: 600,
  },
  thresholdErr: {
    marginTop: 2,
    color: 'red',
    fontWeight: 600,
  },
  customTabPanel: {
    '&.MuiTabPanel-root': {
      padding: '30px 0px',
    },
  },

  actionButtons: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },

  deleteButton: {
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.palette.generalUi.borders}`,
    color: theme.palette.warning.main,
    '&:hover': {
      backgroundColor: theme.palette.generalUi.panelBackground,
    },
  },

  cancelButton: {
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.palette.generalUi.borders}`,
    color: theme.palette.text.default,
    '&:hover': {
      backgroundColor: theme.palette.generalUi.panelBackground,
    },
  },

  submitButton: {
    // inherits color="primary" from the button prop
  },
}))

const tabsMap = [
  { id: '1', name: 'Configuration' },
  { id: '2', name: 'Weather Model Definitions' },
]

const COUNTRY_ALIASES = {
  UK: 'United Kingdom',
  GB: 'United Kingdom',
  USA: 'United States',
  US: 'United States',
}

const BASE_COUNTRIES = countries.map(c => c.name.common)

const BASE_TECHNOLOGIES = ['Wind', 'Solar']
const BASE_TECH_GROUPS = ['OFFSHORE', 'ONSHORE']

function normalizeCountry(name, extra = []) {
  if (!name) return null

  const trimmed = name.trim()

  // check alias
  if (COUNTRY_ALIASES[trimmed]) {
    return COUNTRY_ALIASES[trimmed]
  }

  const validSet = new Set([
    ...BASE_COUNTRIES,
    ...Object.keys(COUNTRY_ALIASES),
    ...extra,
  ])

  return validSet.has(trimmed) ? trimmed : null
}

function extractTemplateMetadata(aggregatedForecastObj) {
  const result = {}
  const templates = Object.values(aggregatedForecastObj || {})
  const combined = templates.join(' ')

  const techMatch = combined.match(/<tech>(.*?)<\/tech>/i)
  if (techMatch) result.tech = techMatch[1]

  const countryMatch = combined.match(/<country>(.*?)<\/country>/i)
  if (countryMatch) result.country = countryMatch[1]

  const techGroupMatch = combined.match(/<techgroup>(.*?)<\/techgroup>/i)
  if (techGroupMatch) result.techGroup = techGroupMatch[1]

  const plantGroupMatch = combined.match(/<plantgroup>(.*?)<\/plantgroup>/i)
  if (plantGroupMatch) result.plantGroup = plantGroupMatch[1]

  return result
}

const toPlantOrNull = val => {
  if (val == null || val === '' || val === 'null') return null
  return val === '{plant}' ? TEMPLATE_OPTIONS.PLANT : val
}

const normalizeToOptions = (val, options) => {
  const v = toPlantOrNull(val)
  if (v == null) return null
  return options.includes(v) ? v : null
}

const buildOptions = ctx => {
  const { tech, techGroup, country, plantGroup } = ctx

  return {
    online: [
      TEMPLATE_OPTIONS.PLANT,
      TEMPLATE_OPTIONS.POWER_PLANT,
      tech ? TEMPLATE_OPTIONS.POWER_PLANT_TECH(tech) : null,
    ].filter(Boolean),

    metering: [
      TEMPLATE_OPTIONS.PLANT,
      TEMPLATE_OPTIONS.POWER_PLANT,
      tech ? TEMPLATE_OPTIONS.POWER_PLANT_TECH(tech) : null,
    ].filter(Boolean),

    curtailment: [TEMPLATE_OPTIONS.PLANT],

    downregulation: [
      TEMPLATE_OPTIONS.PLANT,
      tech ? TEMPLATE_OPTIONS.DOWNREGULATION(tech) : null,
    ].filter(Boolean),

    availability: [
      TEMPLATE_OPTIONS.PLANT,
      tech ? TEMPLATE_OPTIONS.AVAILABILITY(tech) : null,
    ].filter(Boolean),

    redispatch: [TEMPLATE_OPTIONS.PLANT],

    perPlantPowerForecast: [
      TEMPLATE_OPTIONS.PLANT,
      TEMPLATE_OPTIONS.POWER_PERCENTILE_PLANT,
      TEMPLATE_OPTIONS.DSWRF_PERCENTILE_PLANT,
    ],

    combined: [
      TEMPLATE_OPTIONS.PLANT,
      TEMPLATE_OPTIONS.POWER_PERCENTILE_PLANT,
      TEMPLATE_OPTIONS.DSWRF_PERCENTILE_PLANT,
    ],

    countryAggr: [
      TEMPLATE_OPTIONS.COUNTRY_AGGR(tech, country),
      TEMPLATE_OPTIONS.COUNTRY_AGGR_TECH_GROUP(tech, techGroup, country),
    ].filter(Boolean),

    tso: [
      TEMPLATE_OPTIONS.TSO_TECH(tech),
      TEMPLATE_OPTIONS.TSO_TECH_COUNTRY(tech, country),
      TEMPLATE_OPTIONS.TSO_TECH_TECHGROUP(tech, techGroup),
      TEMPLATE_OPTIONS.TSO_TECH_COUNTRY_PLANTGROUP(tech, country, plantGroup),
      TEMPLATE_OPTIONS.TSO_POWER_CORRECTED(tech),
    ].filter(Boolean),

    balancingCircle: [
      TEMPLATE_OPTIONS.BC_TECH(tech),
      TEMPLATE_OPTIONS.BC_TECH_COUNTRY(tech, country),
      TEMPLATE_OPTIONS.BC_TECH_TECHGROUP_COUNTRY(tech, techGroup, country),
    ].filter(Boolean),

    technologyGroupAggr: [
      TEMPLATE_OPTIONS.TECH_GROUP(tech, techGroup, country),
    ].filter(Boolean),

    plantGroupAggr: [TEMPLATE_OPTIONS.PLANT_GROUP(tech, plantGroup)].filter(
      Boolean,
    ),
  }
}

const parseTemplateValues = (template, ctx = {}) => {
  const opts = buildOptions(ctx)
  const aggregatedPowerForecast = template.aggregated_power_forecast || {}

  return {
    ecmwf: toPlantOrNull(template.ecmwf),
    etaec: toPlantOrNull(template.etaec),
    gfseta: toPlantOrNull(template.gfs_eta),
    icon: toPlantOrNull(template.icon),
    ukmo: toPlantOrNull(template.ukmo),
    ukmo100: toPlantOrNull(template.ukmo100),
    windspeed: toPlantOrNull(template.windspeed),
    windspeedOnline: toPlantOrNull(template.windspeed_online),
    arpegeEu: toPlantOrNull(template.arpege_eu),
    arpegeGlobal: toPlantOrNull(template.arpege_global),
    aromeFrHd: toPlantOrNull(template.arome_fr_hd),
    metNordic: toPlantOrNull(template.met_nordic),
    icon2i: toPlantOrNull(template.icon_2i),
    dmi: toPlantOrNull(template.dmi),
    knmiEu: toPlantOrNull(template.knmi_eu),
    knmiNl: toPlantOrNull(template.knmi_nl),
    iconCh1: toPlantOrNull(template.icon_ch1),
    iconCh2: toPlantOrNull(template.icon_ch2),
    aifs: toPlantOrNull(template.aifs),
    iconD2: toPlantOrNull(template.icon_d2),
    iconGlobal: toPlantOrNull(template.icon_global),
    ukmoGlobal: toPlantOrNull(template.ukmo_global),
    ukmo2k: toPlantOrNull(template.ukmo_2k),
    online: normalizeToOptions(template.online, opts.online),
    metering: normalizeToOptions(template.metering, opts.metering),
    curtailment: normalizeToOptions(template.curtailment, opts.curtailment),
    downregulation: normalizeToOptions(
      template.downregulation,
      opts.downregulation,
    ),
    availability: normalizeToOptions(template.availability, opts.availability),
    redispatch: normalizeToOptions(template.redispatch, opts.redispatch),
    perPlantPowerForecast: normalizeToOptions(
      template.per_plant_power_forecast,
      opts.perPlantPowerForecast,
    ),
    combined: normalizeToOptions(template.combined, opts.combined),
    aggregatedPowerForecast: {
      countryAggr: normalizeToOptions(
        aggregatedPowerForecast.country,
        opts.countryAggr,
      ),
      tso: normalizeToOptions(aggregatedPowerForecast.tso, opts.tso),
      balancingCircle: normalizeToOptions(
        aggregatedPowerForecast.balancing_circle,
        opts.balancingCircle,
      ),
      technologyGroupAggr: normalizeToOptions(
        aggregatedPowerForecast.country,
        opts.technologyGroupAggr,
      ),
      plantGroupAggr: normalizeToOptions(
        aggregatedPowerForecast.plant_group,
        opts.plantGroupAggr,
      ),
    },
    icingBinary: template.icing_binary || null,
    icingBinaryPhysical: template.icing_binary_physical || null,
    icingPowerLoss: template.icing_power_loss || null,
    icingProbability: template.icing_probability || null,
  }
}

const TEMPLATE_OPTIONS = Object.freeze({
  PLANT: 'plant',
  POWER_PLANT: '<h><param>POWER</param><plant>{plant}</plant></h>',
  POWER_PLANT_TECH: tech =>
    tech
      ? `<h><param>POWER</param><plant>{plant}</plant><tech>${tech}</tech></h>`
      : '',
  DOWNREGULATION: tech =>
    tech
      ? `<h><param>DOWNREGULATION</param><plant>{plant}</plant><tech>${tech}</tech></h>`
      : '',
  AVAILABILITY: tech =>
    tech
      ? `<h><param>AVAILABILITY</param><plant>{plant}</plant><tech>${tech}</tech></h>`
      : '',
  POWER_PERCENTILE_PLANT:
    '<h><param>POWER</param><percentile>{percentile}</percentile><plant>{plant}</plant></h>',
  DSWRF_PERCENTILE_PLANT:
    '<h><param>DSWRF</param><percentile>{percentile}</percentile><plant>{plant}</plant></h>',
  COUNTRY_AGGR: (tech, country) =>
    tech && country
      ? `<h><param>POWER</param><percentile>{percentile}</percentile><tech>${tech}</tech><country>${country}</country></h>`
      : '',
  COUNTRY_AGGR_TECH_GROUP: (tech, techGroup, country) =>
    tech && techGroup && country
      ? `<h><param>POWER</param><percentile>{percentile}</percentile><tech>${tech}</tech><techgroup>${techGroup}</techgroup><country>${country}</country></h>`
      : '',
  TSO_TECH: tech =>
    tech
      ? `<h><param>POWER</param><percentile>{percentile}</percentile><tech>${tech}</tech><tso>{tso}</tso></h>`
      : '',
  TSO_TECH_COUNTRY: (tech, country) =>
    tech && country
      ? `<h><param>POWER</param><percentile>{percentile}</percentile><tech>${tech}</tech><country>${country}</country><tso>{tso}</tso></h>`
      : '',
  TSO_TECH_TECHGROUP: (tech, techGroup) =>
    tech && techGroup
      ? `<h><param>POWER</param><percentile>{percentile}</percentile><tech>${tech}</tech><techgroup>${techGroup}</techgroup><tso>{tso}</tso></h>`
      : '',
  TSO_TECH_COUNTRY_PLANTGROUP: (tech, country, plantGroup) =>
    tech && country && plantGroup
      ? `<h><param>POWER</param><percentile>{percentile}</percentile><tech>${tech}</tech><country>${country}</country><tso>{tso}</tso><plantgroup>${plantGroup}</plantgroup></h>`
      : '',
  TSO_POWER_CORRECTED: tech =>
    tech
      ? `<h><param>POWER_CORRECTED</param><percentile>{percentile}</percentile><tech>${tech}</tech><tso>{tso}</tso></h>`
      : '',
  BC_TECH: tech =>
    tech
      ? `<h><param>POWER</param><percentile>{percentile}</percentile><tech>${tech}</tech><tso>{tso}</tso><bc>{balancing_circle}</bc></h>`
      : '',
  BC_TECH_COUNTRY: (tech, country) =>
    tech && country
      ? `<h><param>POWER</param><percentile>{percentile}</percentile><tech>${tech}</tech><country>${country}</country><tso>{tso}</tso><bc>{balancing_circle}</bc></h>`
      : '',
  BC_TECH_TECHGROUP_COUNTRY: (tech, techGroup, country) =>
    tech && techGroup && country
      ? `<h><param>POWER</param><percentile>{percentile}</percentile><tech>${tech}</tech><techgroup>${techGroup}</techgroup><country>${country}</country><tso>{tso}</tso><bc>{balancing_circle}</bc></h>`
      : '',
  TECH_GROUP: (tech, techGroup, country) =>
    tech && techGroup && country
      ? `<h><param>POWER</param><percentile>{percentile}</percentile><tech>${tech}</tech><techgroup>${techGroup}</techgroup><country>${country}</country></h>`
      : '',
  PLANT_GROUP: (tech, plantGroup) =>
    tech && plantGroup
      ? `<h><param>POWER</param><percentile>{percentile}</percentile><tech>${tech}</tech><plantgroup>${plantGroup}</plantgroup></h>`
      : '',
  ICING_BINARY:
    '<h><param>ICING_BINARY</param><percentile>{percentile}</percentile><plant>{plant}</plant></h>',
  ICING_BINARY_PHYSICAL:
    '<h><param>ICING_BINARY_PHYSICAL</param><percentile>{percentile}</percentile><plant>{plant}</plant></h>',
  ICING_POWER_LOSS:
    '<h><param>ICING_POWER_LOSS</param><percentile>{percentile}</percentile><plant>{plant}</plant></h>',
  ICING_PROBABILITY:
    '<h><param>ICING_PROBABILITY</param><percentile>{percentile}</percentile><plant>{plant}</plant></h>',
  WINDSPEED: '<h><param>SPEED</param><plant>{plant}</plant></h>',
  WINDSPEED_PERCENTILE:
    '<h><param>SPEED</param><percentile>{percentile}</percentile><plant>{plant}</plant></h>',
})

const initialFormState = {
  setupname: '',
  setupnameError: '',
  alias: '',
  aliasError: '',
  database: '',
  databaseError: '',
  aggregation_definitions: [],
  aggregation_definitionsError: '',
  resolutionMinutes: 15,
  resolutionError: '',
  overviewDahError: true,
  enableNmaeNormalized: false,
  enableFilteredError: false,
  ecmwf: TEMPLATE_OPTIONS.POWER_PERCENTILE_PLANT,
  ecmwfError: '',
  etaec: TEMPLATE_OPTIONS.POWER_PERCENTILE_PLANT,
  etaecError: '',
  gfseta: TEMPLATE_OPTIONS.POWER_PERCENTILE_PLANT,
  gfsetaError: '',
  icon: TEMPLATE_OPTIONS.POWER_PERCENTILE_PLANT,
  iconError: '',
  ukmo: TEMPLATE_OPTIONS.POWER_PERCENTILE_PLANT,
  ukmoError: '',
  ukmo100: TEMPLATE_OPTIONS.POWER_PERCENTILE_PLANT,
  ukmo100Error: '',
  windspeed: null,
  windspeedError: '',
  windspeedOnline: null,
  windspeedOnlineError: '',
  arpegeEu: TEMPLATE_OPTIONS.POWER_PERCENTILE_PLANT,
  arpegeGlobal: TEMPLATE_OPTIONS.POWER_PERCENTILE_PLANT,
  aromeFrHd: TEMPLATE_OPTIONS.POWER_PERCENTILE_PLANT,
  metNordic: TEMPLATE_OPTIONS.POWER_PERCENTILE_PLANT,
  icon2i: TEMPLATE_OPTIONS.POWER_PERCENTILE_PLANT,
  dmi: TEMPLATE_OPTIONS.POWER_PERCENTILE_PLANT,
  knmiEu: TEMPLATE_OPTIONS.POWER_PERCENTILE_PLANT,
  knmiNl: TEMPLATE_OPTIONS.POWER_PERCENTILE_PLANT,
  iconCh1: TEMPLATE_OPTIONS.POWER_PERCENTILE_PLANT,
  iconCh2: TEMPLATE_OPTIONS.POWER_PERCENTILE_PLANT,
  aifs: TEMPLATE_OPTIONS.POWER_PERCENTILE_PLANT,
  iconD2: TEMPLATE_OPTIONS.POWER_PERCENTILE_PLANT,
  iconGlobal: TEMPLATE_OPTIONS.POWER_PERCENTILE_PLANT,
  ukmoGlobal: TEMPLATE_OPTIONS.POWER_PERCENTILE_PLANT,
  ukmo2k: TEMPLATE_OPTIONS.POWER_PERCENTILE_PLANT,
  modelsError: '',
  country: null,
  countryError: '',
  technology: null,
  technologyError: '',
  technologyGroup: null,
  technologyGroupError: '',
  plantGroup: null,
  plantGroupError: '',
  online: TEMPLATE_OPTIONS.PLANT,
  onlineError: '',
  metering: TEMPLATE_OPTIONS.PLANT,
  meteringError: '',
  curtailment: TEMPLATE_OPTIONS.PLANT,
  curtailmentError: '',
  downregulation: TEMPLATE_OPTIONS.PLANT,
  downregulationError: '',
  availability: TEMPLATE_OPTIONS.PLANT,
  availabilityError: '',
  redispatch: TEMPLATE_OPTIONS.PLANT,
  redispatchError: '',
  perPlantPowerForecast: TEMPLATE_OPTIONS.POWER_PERCENTILE_PLANT,
  perPlantPowerForecastError: '',
  combined: TEMPLATE_OPTIONS.POWER_PERCENTILE_PLANT,
  combinedError: '',
  aggregatedPowerForecast: {
    countryAggr: null,
    countryAggrError: '',
    tso: null,
    tsoError: '',
    balancingCircle: null,
    balancingCircleError: '',
    technologyGroupAggr: null,
    technologyGroupAggrError: '',
    plantGroupAggr: null,
    plantGroupAggrError: '',
  },
  icingBinary: null,
  icingBinaryPhysical: null,
  icingPowerLoss: null,
  icingProbability: null,
  users: [],
}

const initialQmFormState = {
  factor: 1.2,
  factorError: '',
  force: false,
  file: null,
  fileName: '',
  fileError: '',
}

const buildMetaOptions = (baseList, value, aliasMap) => {
  if (!value) return baseList

  // if alias map is provided (only for country)
  if (aliasMap && aliasMap[value]) {
    value = aliasMap[value]
  }

  return baseList.includes(value) ? baseList : [...baseList, value]
}

const Setup = props => {
  const classes = useStyles(props)
  const theme = useTheme()
  const {
    history,
    allSetups,
    getAllSetups,
    deleteSetup,
    updateSetup,
    creating,
    apiLoading,
    apiStatusOk,
    match,
    createSetup,
    openQMForm,
    submitQmUpload,
    openQmDialog,
    closeQmDialog,
    resetQmState,
    uploadSuccess,
    thresholdMessage,
    thresholdUpdates,
    thresholdError,
    isThresholdLoading,
  } = props

  const initialConfirmDialog = {
    open: false,
    confirmCallback: null,
    cancelCallback: null,
    title: '',
    content: '',
    color: '',
    type: 'default',
    expectedText: null,
    confirmLabel: 'Delete',
    cancelLabel: 'Cancel',
  }
  const [confirmDialog, setConfirmDialog] = useState(initialConfirmDialog)
  const [loading, setLoading] = useState(true)
  const [setupNotFound, setSetupNotFound] = useState(false)

  const [submitted, setSubmitted] = useState(false)
  const [formState, setFormState] = useState(initialFormState)

  //in case I need disable submit button
  const [attemptedSubmit, setAttemptedSubmit] = useState(false)

  const [countryOptions, setCountryOptions] = useState(BASE_COUNTRIES)
  const [technologyOptions, setTechnologyOptions] = useState(BASE_TECHNOLOGIES)
  const [technologyGroupOptions, setTechnologyGroupOptions] =
    useState(BASE_TECH_GROUPS)
  const [plantGroupOptions, setPlantGroupOptions] = useState([])

  const [hydrated, setHydrated] = useState(false) // exted the spiner time because of filing the form of setup

  /** QM upload */
  const [qmFormState, setQmFormState] = useState(initialQmFormState)
  const [uploadedSetupid, setUploadedSetupid] = useState(null)

  /** TABS */
  const [mainTabValue, setMainTabValue] = useState(tabsMap[0])

  /** Users search */
  const [userSearch, setUserSearch] = useState('')

  const dispatch = useDispatch()

  useEffect(() => {
    if (!allSetups) {
      getAllSetups()
    }
  }, [allSetups, getAllSetups])

  useEffect(() => {
    if (!apiLoading && apiStatusOk && submitted) {
      history.push(`/settings/setups`)
      setSubmitted(false)
    }
  }, [apiLoading, submitted, apiStatusOk])

  useEffect(() => {
    if (creating) {
      setFormState(initialFormState)
      setLoading(false)
      setHydrated(true)
      setSetupNotFound(false)
      return
    } else {
      const fetchSetup = async () => {
        setLoading(true)
        setSetupNotFound(false)
        setHydrated(false)

        try {
          const response = await Api.Setups.getSetup(match.params.name)
          const resSetup = response.data.setup

          if (resSetup) {
            // this is needed for qm threshold update
            setUploadedSetupid(resSetup.id)

            const config = resSetup.config || {}
            const template = config.template_parameter_key || {}

            const metaData = extractTemplateMetadata(
              template.aggregated_power_forecast,
            )

            const rawCountry = metaData.country
            const country = normalizeCountry(
              rawCountry,
              rawCountry ? [rawCountry] : [],
            )

            setCountryOptions(
              buildMetaOptions(
                [...BASE_COUNTRIES, ...Object.keys(COUNTRY_ALIASES)],
                country,
              ),
            )

            // tech
            const technology = metaData.tech || null
            setTechnologyOptions(
              buildMetaOptions(BASE_TECHNOLOGIES, technology),
            )

            // tech group
            const technologyGroup = metaData.techGroup || null
            setTechnologyGroupOptions(
              buildMetaOptions(BASE_TECH_GROUPS, technologyGroup),
            )

            // plant group (no base list → just pass empty [])
            const plantGroup = metaData.plantGroup || null
            setPlantGroupOptions(buildMetaOptions([], plantGroup))
            const parsedTemplate = parseTemplateValues(template, {
              tech: technology,
              techGroup: technologyGroup,
              country: country,
              plantGroup: plantGroup,
            })

            const flattenedSetup = {
              ...initialFormState,
              id: resSetup.id,
              setupname: resSetup.name || '',
              alias: resSetup.alias || '',
              database: config.clickhouse_db || '',
              country,
              technology,
              technologyGroup,
              plantGroup,
              aggregation_definitions: config.aggregation_definitions || [],
              resolutionMinutes: config.resolution_minutes || 15,
              overviewDahError: resSetup.is_overview_dah_error,
              enableNmaeNormalized: resSetup.enable_nmae_normalized,
              enableFilteredError: resSetup.is_error_filtered,
              ecmwf: parsedTemplate.ecmwf,
              etaec: parsedTemplate.etaec,
              gfseta: parsedTemplate.gfseta,
              icon: parsedTemplate.icon,
              ukmo: parsedTemplate.ukmo,
              ukmo100: parsedTemplate.ukmo100,
              windspeed: parsedTemplate.windspeed,
              windspeedOnline: parsedTemplate.windspeed_online,
              arpegeEu: parsedTemplate.arpege_eu,
              arpegeGlobal: parsedTemplate.arpege_global,
              aromeFrHd: parsedTemplate.arome_fr_hd,
              metNordic: parsedTemplate.met_nordic,
              icon2i: parsedTemplate.icon_2i,
              dmi: parsedTemplate.dmi,
              knmiEu: parsedTemplate.knmi_eu,
              knmiNl: parsedTemplate.knmi_nl,
              iconCh1: parsedTemplate.icon_ch1,
              iconCh2: parsedTemplate.icon_ch2,
              aifs: parsedTemplate.aifs,
              iconD2: parsedTemplate.icon_d2,
              iconGlobal: parsedTemplate.icon_global,
              ukmoGlobal: parsedTemplate.ukmo_global,
              ukmo2k: parsedTemplate.ukmo_2k,
              ...parsedTemplate,
              aggregatedPowerForecast: {
                ...initialFormState.aggregatedPowerForecast,
                ...parsedTemplate.aggregatedPowerForecast,
              },
              icingBinary: parsedTemplate.icingBinary,
              icingBinaryPhysical: parsedTemplate.icingBinaryPhysical,
              icingPowerLoss: parsedTemplate.icingPowerLoss,
              icingProbability: parsedTemplate.icingProbability,
              users: [...(resSetup.users || [])].sort((a, b) =>
                (a.username || '').localeCompare(b.username || ''),
              ),
            }

            setFormState(flattenedSetup)
            setHydrated(true)
          } else {
            setSetupNotFound(true)
          }
        } catch (error) {
          console.error('Error fetching setup:', error)
          setSetupNotFound(true)
        } finally {
          setLoading(false)
        }
      }

      fetchSetup()
    }
  }, [creating, match.params.name])

  useEffect(() => {
    if (allSetups) {
      setLoading(false)
    }
  })

  // useEffect(() => {
  //   if (uploadSuccess) {
  //     const timer = setTimeout(() => {
  //       dispatch(closeQmDialog())
  //       dispatch(resetQmState())
  //     }, 6000)

  //     return () => clearTimeout(timer)
  //   }
  // }, [uploadSuccess, dispatch])

  // <--------------- validations --------------->

  const modelMappings = {
    ecmwf: 'ECMWF',
    etaec: 'ETAEC',
    gfseta: 'GFS ETA',
    icon: 'ICON',
    ukmo: 'UKMO',
    ukmo100: 'UKMO 100',
  }

  const isValidName = name => {
    return /^[a-z0-9_]+$/.test(name)
  }

  const validateSetupName = name => {
    if (!name.trim()) {
      return 'Setup name cannot be empty.'
    }
    if (!isValidName(name)) {
      return 'Setup name can only contain lowercase letters, numbers, and underscores.'
    }
    if (creating && allSetups.some(s => s.name === name)) {
      return 'This name is already in use.'
    }
    return ''
  }

  const validateAlias = alias => {
    if (!alias.trim()) {
      return 'Alias cannot be empty.'
    }
    if (!isValidName(alias)) {
      return 'Alias can only contain lowercase letters, numbers, and underscores.'
    }
    if (creating && allSetups.some(s => s.alias === alias)) {
      return 'This alias is already in use.'
    }
    return ''
  }

  const validateDB = name => {
    if (!name.trim()) {
      return 'Database name cannot be empty.'
    }
    if (!isValidName(name)) {
      return 'Database name can only contain lowercase letters, numbers, and underscores.'
    }
  }

  const validateResolution = value => {
    if (!/^\d+$/.test(value)) {
      return 'Resolution must be a whole number.'
    }
    const parsed = parseInt(value, 10)
    if (parsed < 1 || parsed > 120) {
      return 'Resolution must be between 1 and 120.'
    }
    return ''
  }

  const validateDropdown = (value, fieldName) => {
    if (value == null || value === '') {
      return `${fieldName} is required.`
    }
    return ''
  }

  // const validateModelsGroup = fs => {
  //   const anySelected = Object.keys(modelMappings).some(
  //     k => fs[k] != null && fs[k] !== '',
  //   )
  //   return anySelected ? '' : 'At least one model is required.'
  // }

  // <--------------- handlers --------------->

  const handleTextInputChange = field => e => {
    const newVal = e.target.value

    let error = ''
    if (field === 'setupname') {
      error = validateSetupName(newVal)
    } else if (field === 'alias') {
      error = validateAlias(newVal)
    } else if (field === 'database') {
      error = validateDB(newVal)
    } else if (field === 'resolutionMinutes') {
      error = validateResolution(newVal)
    }

    setFormState({
      ...formState,
      [field]: newVal,
      ...(field === 'setupname' && { setupnameError: error }),
      ...(field === 'alias' && { aliasError: error }),
      ...(field === 'database' && { databaseError: error }),
      ...(field === 'resolutionMinutes' && { resolutionError: error }),
    })
  }

  const capitalize = field => {
    if (field === 'perPlantPowerForecast') {
      return 'Per Plant Power Forecast'
    }

    const lowerField = field.toLowerCase()

    // Check for any model keyword and return its mapped value
    if (modelMappings[lowerField]) {
      return modelMappings[lowerField]
    }

    return field.charAt(0).toUpperCase() + field.slice(1)
  }

  const handleDropdownChange = field => (event, newValue) => {
    const error = newValue ? '' : `${capitalize(field)} is required.`
    setFormState(prev => ({
      ...prev,
      [field]: newValue,
      [`${field}Error`]: error,
    }))
  }

  /** Used only for weather models */
  const handleDropdownChangeNoError = field => (_, newVal) => {
    setFormState(prev => ({
      ...prev,
      [field]: newVal,
    }))
  }

  const handleAggregatedPowerForecastChange =
    childField => (event, newValue) => {
      setFormState(prev => ({
        ...prev,
        aggregatedPowerForecast: {
          ...prev.aggregatedPowerForecast,
          [childField]: newValue,
        },
      }))
    }

  const handleDropdownChangeIcing = field => (event, newValue) => {
    setFormState(prev => ({
      ...prev,
      [field]: newValue,
    }))
  }

  const rootKeys = ['online', 'metering', 'downregulation', 'availability']
  const dependencyMap = {
    country: [
      {
        key: 'countryAggr',
        resetOn: [
          () =>
            TEMPLATE_OPTIONS.COUNTRY_AGGR(
              formState.technology,
              formState.country,
            ),
          () =>
            TEMPLATE_OPTIONS.COUNTRY_AGGR_TECH_GROUP(
              formState.technology,
              formState.technologyGroup,
              formState.country,
            ),
        ],
      },
      {
        key: 'tso',
        resetOn: [
          () =>
            TEMPLATE_OPTIONS.TSO_TECH_COUNTRY(
              formState.technology,
              formState.country,
            ),
          () =>
            TEMPLATE_OPTIONS.TSO_TECH_COUNTRY_PLANTGROUP(
              formState.technology,
              formState.country,
              formState.plantGroup,
            ),
        ],
      },
      {
        key: 'balancingCircle',
        resetOn: [
          () =>
            TEMPLATE_OPTIONS.BC_TECH_COUNTRY(
              formState.technology,
              formState.country,
            ),
          () =>
            TEMPLATE_OPTIONS.BC_TECH_TECHGROUP_COUNTRY(
              formState.technology,
              formState.technologyGroup,
              formState.country,
            ),
        ],
      },
      {
        key: 'technologyGroupAggr',
        resetOn: [
          () =>
            TEMPLATE_OPTIONS.TECH_GROUP(
              formState.technology,
              formState.technologyGroup,
              formState.country,
            ),
        ],
      },
    ],

    technology: [
      {
        key: 'online',
        resetOn: [
          () => TEMPLATE_OPTIONS.POWER_PLANT_TECH(formState.technology),
        ],
      },
      {
        key: 'metering',
        resetOn: [
          () => TEMPLATE_OPTIONS.POWER_PLANT_TECH(formState.technology),
        ],
      },
      {
        key: 'downregulation',
        resetOn: [() => TEMPLATE_OPTIONS.DOWNREGULATION(formState.technology)],
      },
      {
        key: 'availability',
        resetOn: [() => TEMPLATE_OPTIONS.AVAILABILITY(formState.technology)],
      },
      {
        key: 'countryAggr',
        resetOn: [
          () =>
            TEMPLATE_OPTIONS.COUNTRY_AGGR(
              formState.technology,
              formState.country,
            ),
          () =>
            TEMPLATE_OPTIONS.COUNTRY_AGGR_TECH_GROUP(
              formState.technology,
              formState.technologyGroup,
              formState.country,
            ),
        ],
      },
      {
        key: 'tso',
        resetOn: [
          () => TEMPLATE_OPTIONS.TSO_TECH(formState.technology),
          () =>
            TEMPLATE_OPTIONS.TSO_TECH_COUNTRY(
              formState.technology,
              formState.country,
            ),
          () =>
            TEMPLATE_OPTIONS.TSO_TECH_TECHGROUP(
              formState.technology,
              formState.technologyGroup,
            ),
          () =>
            TEMPLATE_OPTIONS.TSO_TECH_COUNTRY_PLANTGROUP(
              formState.technology,
              formState.country,
              formState.plantGroup,
            ),
          () => TEMPLATE_OPTIONS.TSO_POWER_CORRECTED(formState.technology),
        ],
      },
      {
        key: 'balancingCircle',
        resetOn: [
          () => TEMPLATE_OPTIONS.BC_TECH(formState.technology),
          () =>
            TEMPLATE_OPTIONS.BC_TECH_COUNTRY(
              formState.technology,
              formState.country,
            ),
          () =>
            TEMPLATE_OPTIONS.BC_TECH_TECHGROUP_COUNTRY(
              formState.technology,
              formState.technologyGroup,
              formState.country,
            ),
        ],
      },
      {
        key: 'technologyGroupAggr',
        resetOn: [
          () =>
            TEMPLATE_OPTIONS.TECH_GROUP(
              formState.technology,
              formState.technologyGroup,
              formState.country,
            ),
        ],
      },
      {
        key: 'plantGroupAggr',
        resetOn: [
          () =>
            TEMPLATE_OPTIONS.PLANT_GROUP(
              formState.technology,
              formState.plantGroup,
            ),
        ],
      },
    ],

    technologyGroup: [
      {
        key: 'countryAggr',
        resetOn: [
          () =>
            TEMPLATE_OPTIONS.COUNTRY_AGGR_TECH_GROUP(
              formState.technology,
              formState.technologyGroup,
              formState.country,
            ),
        ],
      },
      {
        key: 'tso',
        resetOn: [
          () =>
            TEMPLATE_OPTIONS.TSO_TECH_TECHGROUP(
              formState.technology,
              formState.technologyGroup,
            ),
        ],
      },
      {
        key: 'balancingCircle',
        resetOn: [
          () =>
            TEMPLATE_OPTIONS.BC_TECH_TECHGROUP_COUNTRY(
              formState.technology,
              formState.technologyGroup,
              formState.country,
            ),
        ],
      },
      {
        key: 'technologyGroupAggr',
        resetOn: [
          () =>
            TEMPLATE_OPTIONS.TECH_GROUP(
              formState.technology,
              formState.technologyGroup,
              formState.country,
            ),
        ],
      },
    ],

    plantGroup: [
      {
        key: 'tso',
        resetOn: [
          () =>
            TEMPLATE_OPTIONS.TSO_TECH_COUNTRY_PLANTGROUP(
              formState.technology,
              formState.country,
              formState.plantGroup,
            ),
        ],
      },
      {
        key: 'plantGroupAggr',
        resetOn: [
          () =>
            TEMPLATE_OPTIONS.PLANT_GROUP(
              formState.technology,
              formState.plantGroup,
            ),
        ],
      },
    ],
  }

  const handleDropdownChangeOptional = field => (event, newValue) => {
    setFormState(prev => {
      const newState = {
        ...prev,
        [field]: newValue,
        [`${field}Error`]: '',
      }

      if (newValue == null && dependencyMap[field]) {
        const resetAgg = { ...prev.aggregatedPowerForecast }

        dependencyMap[field].forEach(dep => {
          const depValue = rootKeys.includes(dep.key)
            ? prev[dep.key]
            : prev.aggregatedPowerForecast[dep.key]

          // check if dependent’s current value matches any of the resetOn options
          const shouldReset = dep.resetOn.some(fn => fn() === depValue)

          if (shouldReset) {
            if (rootKeys.includes(dep.key)) {
              newState[dep.key] = null
              newState[`${dep.key}Error`] = ''
            } else {
              resetAgg[dep.key] = null
              resetAgg[`${dep.key}Error`] = ''
            }
          }
        })

        newState.aggregatedPowerForecast = resetAgg
      }

      return newState
    })
  }

  // const handleModelDropdownChange = field => (event, newValue) => {
  //   setFormState(prev => {
  //     const next = { ...prev, [field]: newValue }
  //     next.modelsError = validateModelsGroup(next) // only group error
  //     return next
  //   })
  // }

  /** QM validation */
  const validateFactor = raw => {
    const value = parseFloat(raw)

    if (isNaN(value)) {
      return { error: 'Enter a valid number', value: raw }
    }

    if (value < 1 || value > 10) {
      return {
        error: 'Factor must be between 1.0 and 10.0',
        value: raw,
      }
    }

    return {
      error: '',
      value: (Math.round(value * 10) / 10).toFixed(1),
    }
  }

  const validateFile = file => {
    if (!file) {
      return 'CSV file is required'
    }

    if (!file.name.toLowerCase().endsWith('.csv')) {
      return 'Only .csv files allowed'
    }

    return ''
  }

  const validateFileName = name => {
    if (!name || !name.trim()) {
      return 'Filename is required'
    }

    if (!name.toLowerCase().endsWith('.csv')) {
      return 'Only .csv files allowed'
    }

    return ''
  }

  /** QM handlers */
  const handleFactorChange = e => {
    setQmFormState(prev => ({
      ...prev,
      factor: e.target.value, // no validation here, just store text
      factorError: '', // optional: clear error while typing
    }))
  }

  const handleFactorBlur = () => {
    setQmFormState(prev => {
      const { value, error } = validateFactor(prev.factor)

      return {
        ...prev,
        factor: value,
        factorError: error,
      }
    })
  }

  // const handleFileNameChange = e => {
  //   const name = e.target.value

  //   const error = validateFileName(name)

  //   setQmFormState(prev => ({
  //     ...prev,
  //     fileName: name,
  //     fileError: error,
  //   }))
  // }

  const handleFileSelect = e => {
    const file = e.target.files[0]
    if (!file) return

    const error = validateFile(file)

    setQmFormState(prev => ({
      ...prev,
      file,
      fileName: file.name,
      fileError: error,
    }))
  }

  const fileToBase64 = file =>
    new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        const base64 = reader.result.split(',')[1]
        resolve(base64)
      }
      reader.onerror = err => reject(err)
      reader.readAsDataURL(file)
    })

  const handleUpload = async () => {
    // ----- Run validations synchronously -----
    const factorCheck = validateFactor(qmFormState.factor)
    const fileCheck = validateFile(qmFormState.file)
    const nameCheck = validateFileName(qmFormState.fileName)

    const hasError = Boolean(factorCheck.error || fileCheck || nameCheck)

    // ----- Update UI errors -----
    setQmFormState(prev => ({
      ...prev,
      factorError: factorCheck.error || '',
      fileError: fileCheck || nameCheck || '',
    }))

    if (hasError) return

    // ----- Convert CSV to base64 -----
    const base64Csv = await fileToBase64(qmFormState.file)

    const threshold_obj = {
      factor: Number(qmFormState.factor),
      force: qmFormState.force,
      setup_id: uploadedSetupid,
      csv_data: base64Csv,
    }

    submitQmUpload(threshold_obj)
  }

  const handleTabChange = (_, newValue) => {
    const selectedTab = tabsMap.find(tab => tab.id === newValue)
    setMainTabValue(selectedTab)
  }

  const validateForm = () => {
    const errors = {}

    const setupnameError = validateSetupName(formState.setupname)
    if (setupnameError) {
      errors.setupnameError = setupnameError
    }

    const aliasError = validateAlias(formState.alias)
    if (aliasError) {
      errors.aliasError = aliasError
    }

    // Template / config field validations are commented out
    // because buildNewSetup no longer sends the config section.
    // const databaseError = validateDB(formState.database)
    // if (databaseError) {
    //   errors.databaseError = databaseError
    // }

    // const resolutionError = validateResolution(formState.resolutionMinutes)
    // if (resolutionError) {
    //   errors.resolutionError = resolutionError
    // }

    // const onlineError = validateDropdown(formState.online, 'Online')
    // if (onlineError) {
    //   errors.onlineError = onlineError
    // }
    // const meteringError = validateDropdown(formState.metering, 'Metering')
    // if (meteringError) {
    //   errors.meteringError = meteringError
    // }
    // const curtailmentError = validateDropdown(
    //   formState.curtailment,
    //   'Curtailment',
    // )
    // if (curtailmentError) {
    //   errors.curtailmentError = curtailmentError
    // }
    // const downregulationError = validateDropdown(
    //   formState.downregulation,
    //   'Downregulation',
    // )
    // if (downregulationError) {
    //   errors.downregulationError = downregulationError
    // }
    // const availabilityError = validateDropdown(
    //   formState.availability,
    //   'Availability',
    // )
    // if (availabilityError) {
    //   errors.availabilityError = availabilityError
    // }
    // const redispatchError = validateDropdown(formState.redispatch, 'Redispatch')
    // if (redispatchError) {
    //   errors.redispatchError = redispatchError
    // }
    // const perPlantError = validateDropdown(
    //   formState.perPlantPowerForecast,
    //   'Per Plant Power Forecast',
    // )
    // if (perPlantError) {
    //   errors.perPlantPowerForecastError = perPlantError
    // }

    // const combinedError = validateDropdown(formState.combined, 'Combined')
    // if (combinedError) {
    //   errors.combinedError = combinedError
    // }

    // Weather models
    // const ecmwfError = validateDropdown(formState.ecmwf, 'ECMWF')
    // if (ecmwfError) {
    //   errors.ecmwfError = ecmwfError
    // }
    // const etaecError = validateDropdown(formState.etaec, 'ETAEC')
    // if (etaecError) {
    //   errors.etaecError = etaecError
    // }
    // const gfsetaError = validateDropdown(formState.gfseta, 'GFS ETA')
    // if (gfsetaError) {
    //   errors.gfsetaError = gfsetaError
    // }
    // const iconError = validateDropdown(formState.icon, 'ICON')
    // if (iconError) {
    //   errors.iconError = iconError
    // }
    // const ukmoError = validateDropdown(formState.ukmo, 'UKMO')
    // if (ukmoError) {
    //   errors.ukmoError = ukmoError
    // }
    // const ukmo100Error = validateDropdown(formState.ukmo100, 'UKMO 100')
    // if (ukmo100Error) {
    //   errors.ukmo100Error = ukmo100Error
    // }
    // const windspeedError = validateDropdown(formState.windspeed, 'WINDSPEED')
    // if (windspeedError) {
    //   errors.windspeedError = windspeedError
    // }
    // const windspeedOnlineError = validateDropdown(
    //   formState.windspeedOnline,
    //   'WINDSPEED ONLINE',
    // )
    // if (windspeedOnlineError) {
    //   errors.windspeedOnlineError = windspeedOnlineError
    // }

    // Weather models
    // const modelsError = validateModelsGroup(formState)
    // if (modelsError) {
    //   errors.modelsError = modelsError
    // }

    return errors
  }

  const wrapIfPlant = plant => {
    return plant === 'plant' ? `{${plant}}` : `${plant}`
  }

  const aggregated_power_forecast = Object.entries({
    country: formState.aggregatedPowerForecast.countryAggr,
    tso: formState.aggregatedPowerForecast.tso,
    balancing_circle: formState.aggregatedPowerForecast.balancingCircle,
    technology_group: formState.aggregatedPowerForecast.technologyGroupAggr,
    plant_group: formState.aggregatedPowerForecast.plantGroupAggr,
  })
    .filter(([_, value]) => value != null)
    .reduce((acc, [key, value]) => {
      acc[key] = value
      return acc
    }, {})

  const aggregation_definitions = Object.keys(aggregated_power_forecast)

  const buildNewSetup = () => ({
    name: formState.setupname,
    alias: formState.alias,
    is_overview_dah_error: formState.overviewDahError,
    enable_nmae_normalized: formState.enableNmaeNormalized,
    is_error_filtered: formState.enableFilteredError,
  })

  // const buildNewSetup = () => ({
  //   name: formState.setupname,
  //   alias: formState.alias,
  //   is_overview_dah_error: formState.overviewDahError,
  //   enable_nmae_normalized: formState.enableNmaeNormalized,
  //   is_error_filtered: formState.enableFilteredError,
  //   config: {
  //     clickhouse_db: formState.database,
  //     template_parameter_key: {
  //       online: wrapIfPlant(formState.online),
  //       metering: wrapIfPlant(formState.metering),
  //       curtailment: wrapIfPlant(formState.curtailment),
  //       downregulation: wrapIfPlant(formState.downregulation),
  //       availability: wrapIfPlant(formState.availability),
  //       redispatch: wrapIfPlant(formState.redispatch),
  //       combined: wrapIfPlant(formState.combined),
  //       ecmwf: wrapIfPlant(formState.ecmwf),
  //       etaec: wrapIfPlant(formState.etaec),
  //       gfs_eta: wrapIfPlant(formState.gfseta),
  //       icon: wrapIfPlant(formState.icon),
  //       ukmo: wrapIfPlant(formState.ukmo),
  //       ukmo100: wrapIfPlant(formState.ukmo100),
  //       windspeed: wrapIfPlant(formState.windspeed),
  //       windspeed_online: wrapIfPlant(formState.windspeedOnline),
  //       arpege_eu: wrapIfPlant(formState.arpegeEu),
  //       arpege_global: wrapIfPlant(formState.arpegeGlobal),
  //       arome_fr_hd: wrapIfPlant(formState.aromeFrHd),
  //       met_nordic: wrapIfPlant(formState.metNordic),
  //       icon_2i: wrapIfPlant(formState.icon2i),
  //       dmi: wrapIfPlant(formState.dmi),
  //       knmi_eu: wrapIfPlant(formState.knmiEu),
  //       knmi_nl: wrapIfPlant(formState.knmiNl),
  //       icon_ch1: wrapIfPlant(formState.iconCh1),
  //       icon_ch2: wrapIfPlant(formState.iconCh2),
  //       aifs: wrapIfPlant(formState.aifs),
  //       icon_d2: wrapIfPlant(formState.iconD2),
  //       icon_global: wrapIfPlant(formState.iconGlobal),
  //       ukmo_global: wrapIfPlant(formState.ukmoGlobal),
  //       ukmo_2k: wrapIfPlant(formState.ukmo2k),
  //       per_plant_power_forecast: wrapIfPlant(formState.perPlantPowerForecast),
  //       aggregated_power_forecast,
  //       ...(formState.icingBinary != null && {
  //         icing_binary: formState.icingBinary,
  //       }),
  //       ...(formState.icingBinaryPhysical != null && {
  //         icing_binary_physical: formState.icingBinaryPhysical,
  //       }),
  //       ...(formState.icingPowerLoss != null && {
  //         icing_power_loss: formState.icingPowerLoss,
  //       }),
  //       ...(formState.icingProbability != null && {
  //         icing_probability: formState.icingProbability,
  //       }),
  //     },
  //     aggregation_definitions,
  //     resolution_minutes: Number(formState.resolutionMinutes),
  //   },
  // })

  const handleSubmit = () => {
    setAttemptedSubmit(true)
    const errors = validateForm()
    if (Object.keys(errors).length > 0) {
      setFormState(prev => ({ ...prev, ...errors }))
      return
    }

    const updatedSetup = { ...buildNewSetup(), id: formState.id }
    updateSetup(updatedSetup)
    setSubmitted(true)
  }

  const handleCreate = () => {
    setAttemptedSubmit(true)
    const errors = validateForm()
    if (Object.keys(errors).length > 0) {
      setFormState(prev => ({ ...prev, ...errors }))
      return
    }

    const newSetup = buildNewSetup()
    createSetup(newSetup)
    setSubmitted(true)
  }

  const isFormValid = useMemo(() => {
    // If the user hasn't attempted to submit yet, we treat the form as valid.
    if (!attemptedSubmit) {
      return true
    }
    const errors = validateForm()
    return Object.keys(errors).length === 0
  }, [formState, attemptedSubmit])

  const handleCancel = () => {
    history.push(`/settings/setups`)
  }

  const handleDelete = () => {
    setConfirmDialog({
      open: true,
      confirmCallback: () => {
        deleteTheClient(formState.id, formState.setupname)
        setConfirmDialog(initialConfirmDialog)
      },
      cancelCallback: () => setConfirmDialog(initialConfirmDialog),
      title: 'Delete Setup',
      content: (
        <Typography>
          Are you sure you want to delete <b>{formState.setupname}</b>?
        </Typography>
      ),
      color: theme.palette.error.main,
      type: 'warning',
      expectedText: formState.setupname,
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
    })
  }

  const deleteTheClient = (id, name) => {
    deleteSetup(id, name)
    setSubmitted(true)
  }

  // Reusable select (uses formState & handleDropdownChange from closure)
  const TemplateSelect = ({
    label,
    fieldKey,
    options = [TEMPLATE_OPTIONS.PLANT, TEMPLATE_OPTIONS.POWER_PERCENTILE_PLANT],
    formState,
    handleDropdownChange,
    creating = true,
  }) => {
    const errorMsg = formState?.[`${fieldKey}Error`] ?? ''
    return (
      <Grid item xs={12} sm={6} md={3}>
        <CustomInputLabel>{label}</CustomInputLabel>
        <Autocomplete
          id={`${formState[fieldKey]}-${fieldKey}`}
          options={options}
          value={formState[fieldKey] ?? null}
          onChange={handleDropdownChange(fieldKey)}
          renderInput={params => (
            <CustomTextField
              {...params}
              variant="outlined"
              margin="dense"
              size="small"
              fullWidth
              error={!!errorMsg}
              helperText={errorMsg}
              inputProps={{
                ...params.inputProps,
                style: { padding: '1px 5px' },
              }}
            />
          )}
          disabled={!creating} // temporarily disable for existing setups
        />
      </Grid>
    )
  }

  const isQmFormValid = useMemo(() => {
    return !qmFormState.factorError && !qmFormState.fileError
  }, [qmFormState.factorError, qmFormState.fileError])

  const renderQMFormDialog = () => {
    return (
      <Dialog
        open={openQMForm}
        fullWidth
        maxWidth="sm"
        onClose={() => dispatch(closeQmDialog())}
        PaperProps={{
          sx: {
            border: '1px solid',
            borderColor: 'rgba(0,0,0,0.08)', // almost invisible border
            borderRadius: 2, // optional: softer corners
          },
        }}
      >
        <DialogTitle className={classes.qmTitle}>
          QM threshold upload
        </DialogTitle>

        <DialogContent className={classes.dialogContent}>
          <Grid container spacing={3}>
            {/* ===== Factor ===== */}
            <Grid container item alignItems="center" spacing={2}>
              <Grid item xs={2}>
                <Typography className={classes.qmInput}>Factor</Typography>
              </Grid>

              <Grid item xs={6}>
                <CustomTextField
                  className={classes.factorInput}
                  variant="outlined"
                  size="small"
                  fullWidth
                  type="number"
                  value={qmFormState.factor}
                  onBlur={handleFactorBlur}
                  onChange={handleFactorChange}
                  error={Boolean(qmFormState.factorError)}
                  inputProps={{
                    min: 1,
                    max: 10,
                    step: 0.1,
                  }}
                />
                {qmFormState.factorError && (
                  <FormHelperText error sx={{ mt: 0.5 }}>
                    {qmFormState.factorError}
                  </FormHelperText>
                )}
              </Grid>
            </Grid>

            {/* ===== Force ===== */}
            <Grid container item alignItems="center" spacing={2}>
              <Grid item xs={2}>
                <Typography className={classes.qmInput}>Force</Typography>
              </Grid>

              <Grid item>
                <Checkbox
                  checked={qmFormState.force}
                  onChange={e => {
                    setQmFormState(prev => ({
                      ...prev,
                      force: e.target.checked,
                    }))
                  }}
                />
              </Grid>
            </Grid>

            {/* ===== File ===== */}
            <Grid container item alignItems="center" spacing={2}>
              <Grid item xs={2}>
                <Typography className={classes.qmInput}>File</Typography>
              </Grid>

              <Grid item xs={9}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField
                    className={classes.fileInput}
                    size="small"
                    fullWidth
                    value={qmFormState.fileName}
                    placeholder="Choose file..."
                    // onChange={handleFileNameChange}
                    InputProps={{ readOnly: true }}
                  />
                  <Button component="label" variant="outlined">
                    Browse
                    <input
                      type="file"
                      hidden
                      accept=".csv"
                      onChange={handleFileSelect}
                    />
                  </Button>
                </Box>
                {qmFormState.fileError && (
                  <FormHelperText error sx={{ mt: 0.5 }}>
                    {qmFormState.fileError}
                  </FormHelperText>
                )}
              </Grid>
            </Grid>

            <Grid item xs={12} style={{ justifyContent: 'center' }}>
              {isThresholdLoading && (
                <Box display="flex" justifyContent="center" mt={3}>
                  <CircularProgress />
                </Box>
              )}

              {(uploadSuccess || thresholdError) && (
                <Box mt={3}>
                  {/* Success Section */}
                  {uploadSuccess && thresholdUpdates && (
                    <>
                      <Typography style={{ fontWeight: 600 }}>
                        Updated thresholds:
                      </Typography>

                      <Box className={classes.threhsoldUpdates}>
                        {thresholdUpdates.length > 0 ? (
                          thresholdUpdates.map(({ plant_id, threshold }) => (
                            <Typography
                              key={plant_id}
                              style={{ marginBottom: 1 }}
                            >
                              <strong>{plant_id}</strong>:{' '}
                              {threshold.toFixed(2)}
                            </Typography>
                          ))
                        ) : (
                          <Typography className={classes.noThresholdUpdate}>
                            - No thresholds updated
                          </Typography>
                        )}
                      </Box>

                      <Typography className={classes.thresholdMessageOk}>
                        {thresholdMessage}
                      </Typography>
                    </>
                  )}

                  {/* Error Section */}
                  {thresholdError && (
                    <Typography className={classes.thresholdErr}>
                      {thresholdError}
                    </Typography>
                  )}
                </Box>
              )}
            </Grid>
          </Grid>
        </DialogContent>

        {/* ===== Actions ===== */}
        <DialogActions sx={{ px: 3, pb: 2, pt: 2 }}>
          <Button
            onClick={() => {
              dispatch(closeQmDialog())
            }}
            variant="text"
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            disabled={!isQmFormValid}
            onClick={handleUpload}
          >
            Upload
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  const renderConfiguration = () => {
    return (
      <>
        {/* <Typography variant="h4" className={classes.formSubtitle}>
          Configuration
        </Typography> */}

        <div className={classes.tooltipContainerSD}>
          <CustomInputLabel className={classes.subsection}>
            Scope and Datatypes
          </CustomInputLabel>
          <Tooltip
            title="Your selections in this section may unlock additional options in both Template Parameter Keys and Aggregated Power Forecast."
            placement="top"
            arrow
            classes={{
              tooltip: classes.tooltip,
              arrow: classes.arrow,
            }}
          >
            <span>
              <IconButton
                style={{ padding: '0' }}
                aria-label="info"
                disableTouchRipple
              >
                <HelpIcon fontSize="small" className={classes.helpIcon} />
              </IconButton>
            </span>
          </Tooltip>
        </div>
        <Grid container spacing={4}>
          {/** Country */}
          <TemplateSelect
            label="Country"
            fieldKey="country"
            options={countryOptions}
            formState={formState}
            // creating={creating}
            handleDropdownChange={handleDropdownChangeOptional}
          />

          {/** Technology */}
          <TemplateSelect
            label="Technology"
            fieldKey="technology"
            options={technologyOptions}
            formState={formState}
            // creating={creating}
            handleDropdownChange={handleDropdownChangeOptional}
          />

          {/** Technology Group */}
          <TemplateSelect
            label="Technology Group"
            fieldKey="technologyGroup"
            options={technologyGroupOptions}
            formState={formState}
            // creating={creating}
            handleDropdownChange={handleDropdownChangeOptional}
          />

          {/** Plant Group */}
          <TemplateSelect
            label="Plant Group"
            fieldKey="plantGroup"
            options={plantGroupOptions}
            formState={formState}
            // creating={creating}
            handleDropdownChange={handleDropdownChangeOptional}
          />
        </Grid>

        <>
          {/* Template Parameter Keys (subsection) */}
          <CustomInputLabel className={classes.subsection}>
            Template Parameter Keys
          </CustomInputLabel>

          <Grid container spacing={4}>
            {/* Online */}
            <TemplateSelect
              label="Online"
              fieldKey="online"
              options={[
                TEMPLATE_OPTIONS.PLANT,
                TEMPLATE_OPTIONS.POWER_PLANT,
                TEMPLATE_OPTIONS.POWER_PLANT_TECH(formState.technology),
              ]}
              formState={formState}
              // creating={creating}
              handleDropdownChange={handleDropdownChange}
            />

            {/* Metering */}
            <TemplateSelect
              label="Metering"
              fieldKey="metering"
              options={[
                TEMPLATE_OPTIONS.PLANT,
                TEMPLATE_OPTIONS.POWER_PLANT,
                TEMPLATE_OPTIONS.POWER_PLANT_TECH(formState.technology),
              ]}
              formState={formState}
              // creating={creating}
              handleDropdownChange={handleDropdownChange}
            />

            {/* Curtailment */}
            <TemplateSelect
              label="Curtailment"
              fieldKey="curtailment"
              options={[TEMPLATE_OPTIONS.PLANT]}
              formState={formState}
              // creating={creating}
              handleDropdownChange={handleDropdownChange}
            />

            {/* Downregulation */}
            <TemplateSelect
              label="Downregulation"
              fieldKey="downregulation"
              options={[
                TEMPLATE_OPTIONS.PLANT,
                TEMPLATE_OPTIONS.DOWNREGULATION(formState.technology),
              ]}
              formState={formState}
              // creating={creating}
              handleDropdownChange={handleDropdownChange}
            />

            {/* Availability */}
            <TemplateSelect
              label="Availability"
              fieldKey="availability"
              options={[
                TEMPLATE_OPTIONS.PLANT,
                TEMPLATE_OPTIONS.AVAILABILITY(formState.technology),
              ]}
              formState={formState}
              // creating={creating}
              handleDropdownChange={handleDropdownChange}
            />

            {/* Redispatch */}
            <TemplateSelect
              label="Redispatch"
              fieldKey="redispatch"
              options={[TEMPLATE_OPTIONS.PLANT]}
              formState={formState}
              // creating={creating}
              handleDropdownChange={handleDropdownChange}
            />

            {/* Per Plant Power Forecast */}
            <TemplateSelect
              label="Per Plant Power Forecast"
              fieldKey="perPlantPowerForecast"
              options={[
                TEMPLATE_OPTIONS.PLANT,
                TEMPLATE_OPTIONS.POWER_PERCENTILE_PLANT,
                TEMPLATE_OPTIONS.DSWRF_PERCENTILE_PLANT,
              ]}
              formState={formState}
              // creating={creating}
              handleDropdownChange={handleDropdownChange}
            />

            {/* Combined */}
            <TemplateSelect
              label="Combined"
              fieldKey="combined"
              options={[
                TEMPLATE_OPTIONS.PLANT,
                TEMPLATE_OPTIONS.POWER_PERCENTILE_PLANT,
                TEMPLATE_OPTIONS.DSWRF_PERCENTILE_PLANT,
              ]}
              formState={formState}
              // creating={creating}
              handleDropdownChange={handleDropdownChange}
            />

            {formState.technology === 'Wind' && (
              <>
                {/* WINDSPEED */}
                <TemplateSelect
                  label="Windspeed"
                  fieldKey="windspeed"
                  options={[
                    TEMPLATE_OPTIONS.PLANT,
                    TEMPLATE_OPTIONS.WINDSPEED,
                    TEMPLATE_OPTIONS.WINDSPEED_PERCENTILE,
                  ]}
                  formState={formState}
                  handleDropdownChange={handleDropdownChangeNoError}
                />

                {/* WINDSPEED ONLINE */}
                <TemplateSelect
                  label="Windspeed Online"
                  fieldKey="windspeedOnline"
                  options={[
                    TEMPLATE_OPTIONS.PLANT,
                    TEMPLATE_OPTIONS.WINDSPEED,
                    TEMPLATE_OPTIONS.WINDSPEED_PERCENTILE,
                  ]}
                  formState={formState}
                  handleDropdownChange={handleDropdownChangeNoError}
                />
              </>
            )}
          </Grid>

          {/* Aggregated Power Forecast */}
          <CustomInputLabel className={classes.subsection}>
            Aggregated Power Forecast
          </CustomInputLabel>

          <Grid container spacing={4}>
            {/** Country AGGR */}
            <TemplateSelect
              label="Country"
              fieldKey="countryAggr"
              options={[
                TEMPLATE_OPTIONS.COUNTRY_AGGR(
                  formState.technology,
                  formState.country,
                ),
                TEMPLATE_OPTIONS.COUNTRY_AGGR_TECH_GROUP(
                  formState.technology,
                  formState.technologyGroup,
                  formState.country,
                ),
              ].filter(Boolean)}
              formState={formState.aggregatedPowerForecast}
              // creating={creating}
              handleDropdownChange={handleAggregatedPowerForecastChange}
            />

            {/** TSO */}
            <TemplateSelect
              label="TSO"
              fieldKey="tso"
              options={[
                TEMPLATE_OPTIONS.TSO_TECH(formState.technology),
                TEMPLATE_OPTIONS.TSO_TECH_COUNTRY(
                  formState.technology,
                  formState.country,
                ),
                TEMPLATE_OPTIONS.TSO_TECH_TECHGROUP(
                  formState.technology,
                  formState.technologyGroup,
                ),
                TEMPLATE_OPTIONS.TSO_TECH_COUNTRY_PLANTGROUP(
                  formState.technology,
                  formState.country,
                  formState.plantGroup,
                ),
                TEMPLATE_OPTIONS.TSO_POWER_CORRECTED(formState.technology),
              ].filter(Boolean)}
              formState={formState.aggregatedPowerForecast}
              // creating={creating}
              handleDropdownChange={handleAggregatedPowerForecastChange}
            />

            {/** Balancing Circle */}
            <TemplateSelect
              label="Balancing Circle"
              fieldKey="balancingCircle"
              options={[
                TEMPLATE_OPTIONS.BC_TECH(formState.technology),
                TEMPLATE_OPTIONS.BC_TECH_COUNTRY(
                  formState.technology,
                  formState.country,
                ),
                TEMPLATE_OPTIONS.BC_TECH_TECHGROUP_COUNTRY(
                  formState.technology,
                  formState.technologyGroup,
                  formState.country,
                ),
              ].filter(Boolean)}
              formState={formState.aggregatedPowerForecast}
              // creating={creating}
              handleDropdownChange={handleAggregatedPowerForecastChange}
            />

            {/** Technology Group */}
            <TemplateSelect
              label="Technology Group"
              fieldKey="technologyGroupAggr"
              options={[
                TEMPLATE_OPTIONS.TECH_GROUP(
                  formState.technology,
                  formState.technologyGroup,
                  formState.country,
                ),
              ].filter(Boolean)}
              formState={formState.aggregatedPowerForecast}
              // creating={creating}
              handleDropdownChange={handleAggregatedPowerForecastChange}
            />

            {/** Plant Group */}
            <TemplateSelect
              label="Plant Group"
              fieldKey="plantGroupAggr"
              options={[
                TEMPLATE_OPTIONS.PLANT_GROUP(
                  formState.technology,
                  formState.plantGroup,
                ),
              ].filter(Boolean)}
              formState={formState.aggregatedPowerForecast}
              // creating={creating}
              handleDropdownChange={handleAggregatedPowerForecastChange}
            />
          </Grid>

          {/* Icing */}
          <CustomInputLabel className={classes.subsection}>
            Icing
          </CustomInputLabel>
          <Grid container spacing={4}>
            {/** Icing Binary */}
            <TemplateSelect
              label="Icing Binary"
              fieldKey="icingBinary"
              options={[TEMPLATE_OPTIONS.ICING_BINARY]}
              formState={formState}
              // creating={creating}
              handleDropdownChange={handleDropdownChangeIcing}
            />
            {/** Icing Binary Physical */}
            <TemplateSelect
              label="Icing Binary Physical"
              fieldKey="icingBinaryPhysical"
              options={[TEMPLATE_OPTIONS.ICING_BINARY_PHYSICAL]}
              formState={formState}
              // creating={creating}
              handleDropdownChange={handleDropdownChangeIcing}
            />
            {/** Icing Power Loss */}
            <TemplateSelect
              label="Icing Power Loss"
              fieldKey="icingPowerLoss"
              options={[TEMPLATE_OPTIONS.ICING_POWER_LOSS]}
              formState={formState}
              // creating={creating}
              handleDropdownChange={handleDropdownChangeIcing}
            />
            {/** Icing Probability */}
            <TemplateSelect
              label="Icing Probability"
              fieldKey="icingProbability"
              options={[TEMPLATE_OPTIONS.ICING_PROBABILITY]}
              formState={formState}
              // creating={creating}
              handleDropdownChange={handleDropdownChangeIcing}
            />
          </Grid>
        </>
      </>
    )
  }

  const renderWeatherModelDefinitions = () => {
    return (
      <>
        {/**Forecast Models */}
        {/* <CustomInputLabel className={classes.subsection}>
          Weather Model Definitions
        </CustomInputLabel> */}
        {/* <FormHelperText
          id="component-error-text"
          sx={{ color: '#C63C3C', whiteSpace: 'nowrap' }}
        >
          {formState.modelsError}
        </FormHelperText> */}
        <Grid container spacing={4}>
          {/* ECMWF */}
          <TemplateSelect
            label="ECMWF"
            fieldKey="ecmwf"
            formState={formState}
            // creating={creating}
            // handleDropdownChange={handleDropdownChange}
            handleDropdownChange={handleDropdownChangeNoError}
          />

          {/* ETAEC */}
          <TemplateSelect
            label="ETAEC"
            fieldKey="etaec"
            formState={formState}
            // creating={creating}
            // handleDropdownChange={handleDropdownChange}
            handleDropdownChange={handleDropdownChangeNoError}
          />

          {/* GFS ETA */}
          <TemplateSelect
            label="GFS ETA"
            fieldKey="gfseta"
            formState={formState}
            // creating={creating}
            // handleDropdownChange={handleDropdownChange}
            handleDropdownChange={handleDropdownChangeNoError}
          />

          {/* ICON */}
          <TemplateSelect
            label="ICON"
            fieldKey="icon"
            formState={formState}
            // creating={creating}
            // handleDropdownChange={handleDropdownChange}
            handleDropdownChange={handleDropdownChangeNoError}
          />

          {/* UKMO */}
          <TemplateSelect
            label="UKMO"
            fieldKey="ukmo"
            formState={formState}
            // creating={creating}
            // handleDropdownChange={handleDropdownChange}
            handleDropdownChange={handleDropdownChangeNoError}
          />

          {/* UKMO100 */}
          <TemplateSelect
            label="UKMO100"
            fieldKey="ukmo100"
            formState={formState}
            // creating={creating}
            // handleDropdownChange={handleDropdownChange}
            handleDropdownChange={handleDropdownChangeNoError}
          />

          {/* ARPEGE_EU */}
          <TemplateSelect
            label="ARPEGE EU"
            fieldKey="arpegeEu"
            formState={formState}
            // creating={creating}
            // handleDropdownChange={handleDropdownChange}
            handleDropdownChange={handleDropdownChangeNoError}
          />

          {/* ARPEGE_GLOBAL */}
          <TemplateSelect
            label="ARPEGE GLOBAL"
            fieldKey="arpegeGlobal"
            formState={formState}
            // creating={creating}
            // handleDropdownChange={handleDropdownChange}
            handleDropdownChange={handleDropdownChangeNoError}
          />

          {/* AROME_FR_HD */}
          <TemplateSelect
            label="AROME FR HD"
            fieldKey="aromeFrHd"
            formState={formState}
            // creating={creating}
            // handleDropdownChange={handleDropdownChange}
            handleDropdownChange={handleDropdownChangeNoError}
          />

          {/* MET_NORDIC */}
          <TemplateSelect
            label="MET NORDIC"
            fieldKey="metNordic"
            formState={formState}
            // creating={creating}
            // handleDropdownChange={handleDropdownChange}
            handleDropdownChange={handleDropdownChangeNoError}
          />

          {/* ICON_2I */}
          <TemplateSelect
            label="ICON 2I"
            fieldKey="icon2i"
            formState={formState}
            // creating={creating}
            // handleDropdownChange={handleDropdownChange}
            handleDropdownChange={handleDropdownChangeNoError}
          />

          {/* DMI */}
          <TemplateSelect
            label="DMI"
            fieldKey="dmi"
            formState={formState}
            // creating={creating}
            // handleDropdownChange={handleDropdownChange}
            handleDropdownChange={handleDropdownChangeNoError}
          />
          {/* KNMI_EU */}
          <TemplateSelect
            label="KNMI EU"
            fieldKey="knmiEu"
            formState={formState}
            // creating={creating}
            // handleDropdownChange={handleDropdownChange}
            handleDropdownChange={handleDropdownChangeNoError}
          />
          {/* KNMI_NL */}
          <TemplateSelect
            label="KNMI NL"
            fieldKey="knmiNl"
            formState={formState}
            // creating={creating}
            // handleDropdownChange={handleDropdownChange}
            handleDropdownChange={handleDropdownChangeNoError}
          />
          {/* ICON_CH1 */}
          <TemplateSelect
            label="ICON CH1"
            fieldKey="iconCh1"
            formState={formState}
            // creating={creating}
            // handleDropdownChange={handleDropdownChange}
            handleDropdownChange={handleDropdownChangeNoError}
          />
          {/* ICON_CH2 */}
          <TemplateSelect
            label="ICON CH2"
            fieldKey="iconCh2"
            formState={formState}
            // creating={creating}
            // handleDropdownChange={handleDropdownChange}
            handleDropdownChange={handleDropdownChangeNoError}
          />
          {/* AIFS */}
          <TemplateSelect
            label="AIFS"
            fieldKey="aifs"
            formState={formState}
            // creating={creating}
            // handleDropdownChange={handleDropdownChange}
            handleDropdownChange={handleDropdownChangeNoError}
          />
          {/* ICON_D2 */}
          <TemplateSelect
            label="ICON D2"
            fieldKey="iconD2"
            formState={formState}
            // creating={creating}
            // handleDropdownChange={handleDropdownChange}
            handleDropdownChange={handleDropdownChangeNoError}
          />
          {/* ICON_GLOBAL */}
          <TemplateSelect
            label="ICON GLOBAL"
            fieldKey="iconGlobal"
            formState={formState}
            // creating={creating}
            // handleDropdownChange={handleDropdownChange}
            handleDropdownChange={handleDropdownChangeNoError}
          />
          {/* UKMO_GLOBAL */}
          <TemplateSelect
            label="UKMO GLOBAL"
            fieldKey="ukmoGlobal"
            formState={formState}
            // creating={creating}
            // handleDropdownChange={handleDropdownChange}
            handleDropdownChange={handleDropdownChangeNoError}
          />
          {/* UKMO_2K */}
          <TemplateSelect
            label="UKMO 2K"
            fieldKey="ukmo2k"
            formState={formState}
            // creating={creating}
            // handleDropdownChange={handleDropdownChange}
            handleDropdownChange={handleDropdownChangeNoError}
          />
        </Grid>
      </>
    )
  }

  /** Used only for creating setup or if have some icing values */
  // const hasIcingValues =
  //   formState.icingBinary ||
  //   formState.icingBinaryPhysical ||
  //   formState.icingPowerLoss ||
  //   formState.icingProbability

  const renderForm = () => {
    return (
      <Grid container spacing={4} className={classes.formContainer}>
        {/* DETAILS Section */}
        <Grid item xs={12}>
          <CustomInputLabel className={classes.subsection}>
            Details
          </CustomInputLabel>
          <Grid container spacing={4}>
            {/* Setup Name */}
            <Grid item xs={12} sm={6} md={3}>
              <CustomInputLabel>Setup Name</CustomInputLabel>
              <CustomTextField
                variant="outlined"
                margin="dense"
                size="small"
                fullWidth
                type="text"
                onChange={handleTextInputChange('setupname')}
                value={formState.setupname}
                error={Boolean(formState.setupnameError)}
                helperText={formState.setupnameError}
                disabled={!creating} // add temporary disable for existing setups in order to not edit
              />
            </Grid>
            {/* Alias */}
            <Grid item xs={12} sm={6} md={3}>
              <CustomInputLabel>Alias</CustomInputLabel>
              <CustomTextField
                variant="outlined"
                margin="dense"
                size="small"
                fullWidth
                type="text"
                onChange={handleTextInputChange('alias')}
                value={formState.alias}
                error={Boolean(formState.aliasError)}
                helperText={formState.aliasError}
                // disabled={!creating} // add temporary disable for existing setups in order to not edit
              />
            </Grid>

            {/** QM upload */}
            {!creating && (
              <Grid item xs={12} sm={6} md={3} style={{ marginLeft: 'auto' }}>
                <PrimaryButton
                  variant="contained"
                  className={classes.qmUploadBtn}
                  onClick={() => dispatch(openQmDialog())}
                  // startIcon={<AddIcon />}
                >
                  Upload QM Threshold
                </PrimaryButton>
                {renderQMFormDialog()}
              </Grid>
            )}
          </Grid>
        </Grid>

        {/** Clickhouse DB */}
        <Grid item xs={12} sm={6} md={3}>
          <div className={classes.tooltipContainer}>
            <CustomInputLabel>ClickHouse DB</CustomInputLabel>
            <Tooltip
              title="It is recommended that the DB name match the setup name using an underscore."
              placement="top"
              arrow
              classes={{
                tooltip: classes.tooltip,
                arrow: classes.arrow,
              }}
            >
              <span>
                <IconButton
                  style={{ padding: '0' }}
                  aria-label="info"
                  disableTouchRipple
                >
                  <HelpIcon fontSize="small" className={classes.helpIcon} />
                </IconButton>
              </span>
            </Tooltip>
          </div>
          <CustomTextField
            variant="outlined"
            margin="dense"
            size="small"
            fullWidth
            type="text"
            onChange={handleTextInputChange('database')}
            value={formState.database}
            error={Boolean(formState.databaseError)}
            helperText={formState.databaseError}
            disabled={!creating} // add temporary disable for existing setups in order to not edit
          />
        </Grid>

        {/* Resolution Minutes */}
        <Grid item xs={12} sm={6} md={3}>
          <div className={classes.tooltipContainer}>
            <CustomInputLabel>Resolution Minutes</CustomInputLabel>
            <Tooltip
              title="Recommended values are 1, 10, 15, 30 and 60."
              placement="top"
              arrow
              classes={{
                tooltip: classes.tooltip,
                arrow: classes.arrow,
              }}
            >
              <span>
                <IconButton
                  style={{ padding: '0' }}
                  aria-label="info"
                  disableTouchRipple
                >
                  <HelpIcon fontSize="small" className={classes.helpIcon} />
                </IconButton>
              </span>
            </Tooltip>
          </div>
          <CustomTextField
            variant="outlined"
            margin="dense"
            size="small"
            fullWidth
            type="text"
            onChange={handleTextInputChange('resolutionMinutes')}
            value={formState.resolutionMinutes}
            error={Boolean(formState.resolutionError)}
            helperText={formState.resolutionError}
            disabled={!creating} // add temporary disable for existing setups in order to not edit
          />
        </Grid>

        <Grid
          item
          md={6}
          sx={{ display: { xs: 'none', sm: 'none', md: 'block' } }}
        />

        {/** Show DAH Error */}
        <Grid item xs={12} sm={3} md={3}>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <CustomInputLabel>Enable Overview DAH Error</CustomInputLabel>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Switch
              color="primary"
              size="small"
              checked={formState.overviewDahError}
              onChange={(_, checked) => {
                setFormState(prev => ({
                  ...prev,
                  overviewDahError: checked,
                }))
              }}
            />
          </Box>
        </Grid>

        {/** Show NMAE */}
        <Grid item xs={12} sm={3} md={3}>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <CustomInputLabel>
              Show NMAE Normalized by Available Capacity
            </CustomInputLabel>
            {/* <Tooltip
                  title="Enable to include 'Latest' among the available Forecast Types."
                  placement="top"
                  arrow
                  classes={{
                    tooltip: classes.tooltip,
                    arrow: classes.arrow,
                  }}
                >
                  <IconButton size="small" disableTouchRipple sx={{ p: 0 }}>
                    <HelpIcon fontSize="small" className={classes.helpIcon} />
                  </IconButton>
                </Tooltip> */}
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Switch
              color="primary"
              size="small"
              checked={formState.enableNmaeNormalized ?? false}
              onChange={(_, checked) => {
                setFormState(prev => ({
                  ...prev,
                  enableNmaeNormalized: checked,
                }))
              }}
            />
          </Box>
        </Grid>

        {/** Show Metrics Filtered Error */}
        <Grid item xs={12} sm={3} md={3}>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <CustomInputLabel>Enable Metrics Filtered Error</CustomInputLabel>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Switch
              color="primary"
              size="small"
              checked={formState.enableFilteredError}
              onChange={(_, checked) => {
                setFormState(prev => ({
                  ...prev,
                  enableFilteredError: checked,
                }))
              }}
            />
          </Box>
        </Grid>

        <Grid
          item
          sm={3}
          md={3}
          sx={{ display: { xs: 'none', sm: 'block', md: 'block' } }}
        />

        {/** Users */}
        {!creating && formState.users?.length > 0 && (
          <Grid item xs={12} sm={6} md={3}>
            <CustomInputLabel className={classes.subsection}>
              Users
            </CustomInputLabel>
            <CustomTextField
              variant="outlined"
              margin="dense"
              size="small"
              fullWidth
              type="text"
              placeholder="Search users..."
              value={userSearch}
              onChange={e => setUserSearch(e.target.value)}
              InputProps={{
                endAdornment: userSearch && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setUserSearch('')}
                      aria-label="Clear search"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {(() => {
              const query = userSearch.trim().toLowerCase()
              const filteredUsers = query
                ? formState.users.filter(
                    user =>
                      (user.username || '').toLowerCase().includes(query) ||
                      (user.email || '').toLowerCase().includes(query),
                  )
                : formState.users

              return (
                <List className={classes.usersList} disablePadding>
                  {filteredUsers.length > 0 ? (
                    filteredUsers.map(user => (
                      <ListItemButton
                        key={user.id}
                        dense
                        className={classes.userListItem}
                        onClick={() =>
                          history.push(`/settings/users/${user.id}`)
                        }
                      >
                        <ListItemText
                          primary={user.username}
                          secondary={user.email}
                          primaryTypographyProps={{ variant: 'body2' }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItemButton>
                    ))
                  ) : (
                    <Typography className={classes.noUsersMatch}>
                      - No users match
                    </Typography>
                  )}
                </List>
              )
            })()}
          </Grid>
        )}

        {/** TAB section */}
        {/* <Grid item xs={12}>
          <TabContext value={mainTabValue.id}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <TabList
                onChange={handleTabChange}
                aria-label="Setup configuration"
                TabIndicatorProps={{
                  style: { backgroundColor: '#368ED8' },
                }}
              >
                <Tab
                  style={{
                    color: mainTabValue.id === '1' ? '#368ED8' : 'inherit',
                  }}
                  label="Configuration"
                  value="1"
                ></Tab>
                <Tab
                  style={{
                    color: mainTabValue.id === '2' ? '#368ED8' : 'inherit',
                  }}
                  label="Weather Model Definitions"
                  value="2"
                ></Tab>
              </TabList>
            </Box>

            <TabPanel value="1" className={classes.customTabPanel}>
              {renderConfiguration()}
            </TabPanel>
            <TabPanel value="2" className={classes.customTabPanel}>
              {renderWeatherModelDefinitions()}
            </TabPanel>
          </TabContext>
        </Grid> */}
      </Grid>
    )
  }

  const renderActionButtons = () => {
    return (
      <div className={classes.actionButtons}>
        {!creating && (
          <CustomButton
            onClick={handleDelete}
            disabled={apiLoading}
            variant="contained"
            disableElevation
            className={classes.deleteButton}
          >
            Delete
          </CustomButton>
        )}
        <CustomButton
          onClick={handleCancel}
          disabled={apiLoading}
          variant="contained"
          disableElevation
          className={classes.cancelButton}
        >
          Cancel
        </CustomButton>

        <CustomButton
          onClick={creating ? handleCreate : handleSubmit}
          disabled={!isFormValid || apiLoading}
          variant="contained"
          disableElevation
          color="primary"
          className={classes.submitButton}
        >
          {apiLoading ? <CircularProgress size={14} /> : 'Submit'}
        </CustomButton>
      </div>
    )
  }

  return (
    <Navigation history={history}>
      {loading ? (
        <div className={classes.centerLoading}>
          <CircularProgress />
        </div>
      ) : setupNotFound ? (
        <Typography className={classes.centerLoading} variant="h2">
          Setup not found
        </Typography>
      ) : hydrated ? (
        <>
          <ConfirmationDialog {...confirmDialog} />
          <div className={classes.mainContainer}>
            <div className={classes.headerContainer}>
              <Typography variant="h2" className={classes.title}>
                {formState.setupname}
              </Typography>
              {renderActionButtons()}
            </div>
            {renderForm()}
          </div>
        </>
      ) : (
        <div className={classes.centerLoading}>
          <CircularProgress /> {/* fallback until hydrated */}
        </div>
      )}
    </Navigation>
  )
}

const mapStateToProps = state => {
  return {
    allSetups: state.setups,
    apiLoading: state.status.loading,
    apiStatusOk: state.status.snackbar.severity,
    openQMForm: state.qm.dialogOpen,
    uploadSuccess: state.qm.uploadSuccess,
    thresholdUpdates: state.qm.thresholdUpdates,
    thresholdMessage: state.qm.thresholdMessage,
    isThresholdLoading: state.qm.isLoading,
    thresholdError: state.qm.error,
  }
}

export default connect(mapStateToProps, {
  getAllSetups,
  deleteSetup,
  updateSetup,
  createSetup,
  submitQmUpload,
  openQmDialog,
  closeQmDialog,
  resetQmState,
})(Setup)
